import { fromNow, stamp } from '../inventoree/format'

/**
 * Formatele pe care le foloseau coloanele listei de tichete: cele doua
 * durate din `Convert` (public/js/general.js), scurtarea numelor
 * (`String.prototype.trunc`) si data lunga a tooltipului
 * (`moment(x).tz(TIME_ZONE).format('LLL')`).
 *
 * Scrise aici, fara moment.js si fara moment-timezone (300 KB + baza de fusuri).
 *
 * O DIFERENTA, SPUSA PE FATA: portalul muta ora in fusul scris in
 * `TIME_ZONE` inainte s-o arate in tooltip. Aici se arata ora BROWSERULUI.
 * Pentru un om care lucreaza in fusul instantei e acelasi lucru; pentru unul
 * dintr-alt fus, nu. Cand tokenul v4 va duce si fusul instantei, se pune aici -
 * pana atunci e o diferenta stiuta, nu una ascunsa.
 */

/** Convert.FormatSecondsAsDurationString: "1day 2h 3min 4s", "0s" cand nu e nimic. */
export function durationString(seconds: unknown): string {
  let left = Number(seconds)
  if (!Number.isFinite(left)) return '0s'

  let s = ''
  const days = Math.floor(left / 86400)
  if (days >= 1) {
    s += days.toString() + 'day' + (days === 1 ? ' ' : 's ')
    left -= days * 24 * 3600
  }
  const hours = Math.floor(left / 3600)
  if (hours >= 1) {
    s += hours.toString() + 'h '
    left -= hours * 3600
  }
  const minutes = Math.floor(left / 60)
  if (minutes >= 1) {
    s += minutes.toString() + 'min '
    left -= minutes * 60
  }
  if (left > 0) s += Math.trunc(left).toString() + 's'
  if (s === '') s += '0s'
  return s
}

/**
 * Convert.FormatSecondsAsHoursString: sub un minut spune asa, iar minutele se
 * arata numai cand n-au trecut zile. Sirul se termina cu spatiu, ca in portal.
 */
export function hoursString(seconds: unknown): string {
  let left = Number(seconds)
  if (!Number.isFinite(left)) return ''
  if (left < 60) return 'less than 1min'

  let s = ''
  const days = Math.floor(left / 86400)
  if (days >= 1) {
    s += days.toString() + 'day' + (days === 1 ? ' ' : 's ')
    left -= days * 24 * 3600
  }
  const hours = Math.floor(left / 3600)
  if (hours >= 1) {
    s += hours.toString() + 'h '
    left -= hours * 3600
  }
  const minutes = Math.floor(left / 60)
  if (days === 0 && minutes >= 1) {
    s += minutes.toString() + 'min '
  }
  return s
}

/**
 * String.prototype.trunc din general.js: peste `n` caractere taie la `n - 1`
 * si pune trei puncte. Entitatea `&hellip;` se pastreaza - celula e HTML.
 */
export function trunc(value: string, n: number): string {
  return value.length > n ? value.slice(0, n - 1) + '&hellip;' : value
}

/**
 * Acelasi lucru, dar cu CARACTERUL adevarat in loc de entitate.
 *
 * DE CE DOUA FUNCTII SI NU UNA
 * ----------------------------
 *
 * `trunc` de mai sus e fidela portalului (`String.prototype.trunc` din
 * `public/js/general.js`): da `&hellip;`, care se vede ca „…" numai daca sirul
 * ajunge intr-un loc randat ca HTML - celulele tabelului, prin
 * `dangerouslySetInnerHTML`. Pus intr-un nod de TEXT React, acelasi sir se vede
 * literal, cu tot cu `&` si `;`.
 *
 * Lista scrie in celule, fisa tichetului scrie text, si fiecare si-a transcris
 * varianta buna pentru ea. Contopite intr-una singura, una s-ar strica in
 * tacere: fie apare „&hellip;" pe fisa, fie lista nu mai e identica cu markupul
 * vechi. De-aia sunt doua, cu numele care spune care unde merge.
 */
export function truncText(text: string, length: number): string {
  return text.length > length ? text.slice(0, length - 1) + '\u2026' : text
}

const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** moment(when).format('LLL') pentru engleza: "September 7, 2026 10:05 AM". */
export function longDateTime(when: Date): string {
  const hour24 = when.getHours()
  const suffix = hour24 < 12 ? 'AM' : 'PM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  const minutes = String(when.getMinutes()).padStart(2, '0')

  return `${LONG_MONTHS[when.getMonth()]} ${when.getDate()}, ${when.getFullYear()} ${hour12}:${minutes} ${suffix}`
}

/** `moment(value).fromNow()` pe un timp scris de server; gol cand nu e o data. */
export function agoOrEmpty(value: string, now: Date = new Date()): string {
  const when = stamp(value)
  return when === null ? '' : fromNow(when, now)
}
